import logger from '../../log';
import LRUBytesCache from './LRUBytesCache';
import IndexedDBCache from './IndexedDBCache';
import {
  CacheConfig,
  CacheStore,
  StoreEvents,
  StoreType,
  CacheData
} from './cache/interfaces';

const log = logger.getLogger(__filename);

function getStore({
  config,
  events
}: {
  config: CacheConfig;
  events: StoreEvents;
}): CacheStore<CacheData> {
  switch (config.type) {
    case StoreType.INDEXED_DB:
      return new IndexedDBCache(config, events);
    default:
      return new LRUBytesCache(config, events);
  }
}

export default class ObjectCache implements CacheStore<CacheData> {
  config: CacheConfig;
  store: CacheStore<CacheData>;
  newContentHandlers: ((key: string) => boolean)[];
  evictHandlers: ((key: string) => boolean)[];

  constructor(config?: CacheConfig) {
    this.evictHandlers = [];
    this.newContentHandlers = [];

    this.config = config || {
      type: StoreType.MAP,
      maxBytes: 256000000,
      id: new Date().toString()
    };
    this.store = getStore({
      config: this.config,
      events: {
        ondelete: ({ key }) => {
          this.evictHandlers = this.evictHandlers.filter(handler => {
            try {
              return !handler(key);
            } catch (e) {
              log.warn('Exception thrown while executing evict handler', e);
              return false;
            }
          });
        },
        onset: ({ key }) => {
          this.newContentHandlers.forEach(listener => {
            try {
              listener(key);
            } catch (e) {
              log.warn('Could not execute listener for new content', listener);
            }
          });
        }
      }
    });
  }

  async initialize(): Promise<void> {
    try {
      return this.store.initialize();
    } catch (e) {
      // logging error is handled in the impl
      this.store = getStore({
        // if we fail to init, fall back to the default map cache.
        config: { ...this.config, type: StoreType.MAP },
        events: {
          ondelete: ({ key }) => {
            this.evictHandlers = this.evictHandlers.filter(
              handler => !handler(key)
            );
          },
          onset: ({ key: key_2 }) => {
            this.newContentHandlers.forEach(listener => {
              try {
                listener(key_2);
              } catch (e) {
                log.warn(
                  'Could not execute listener for new content',
                  listener
                );
              }
            });
          }
        }
      });
      return this.store.initialize();
    }
  }

  get(key: string): Promise<CacheData | undefined> {
    return this.store.get(key);
  }

  set(key: string, value: CacheData): Promise<boolean> {
    return this.store.set(key, value);
  }

  delete(key: string): Promise<boolean> {
    return this.store.delete(key);
  }

  has(key: string): boolean {
    return this.store.has(key);
  }

  keys(): IterableIterator<string> {
    return this.store.keys();
  }

  clear(): void {
    this.store.clear();
  }

  getPayloadSize(key: string): Promise<number> {
    return this.store.getPayloadSize(key);
  }

  getTimestamp(key: string): Promise<number> {
    return this.store.getTimestamp(key);
  }

  onEvict(handler: (data: CacheData) => boolean): void {
    this.evictHandlers.push(handler);
  }

  onNewContent(handler: (data: CacheData) => boolean): void {
    this.newContentHandlers.push(handler);
  }
}
